/* ============================================================================
 * Types
 * ========================================================================== */

export interface SearchResult {
	id: string | number;
	title: string;
	description: string;
	category: string;
	price: string;
	image?: string;
	icon?: string;
	metadata?: Record<string, any>;
	available?: boolean;
	oldPrice?: number | null;
}

export interface SearchOptions {
	limit?: number;
	offset?: number;
	sortBy?: string;
	sortOrder?: "asc" | "desc";
	filters?: Record<string, any>;
}

export interface SearchResponse {
	results: SearchResult[];
	total: number;
	took: number;
	facets?: Record<string, any>;
	provider?: string;
}

export interface HealthCheckResponse {
	healthy: boolean;
	message?: string;
}

/* ============================================================================
 * Base service (contract)
 * ========================================================================== */

export abstract class SearchService {
	abstract initialize(config: Record<string, any>): Promise<void>;

	abstract search(query: string, options?: SearchOptions): Promise<SearchResponse>;

	abstract suggest(query: string, options?: SearchOptions): Promise<string[]>;

	abstract getFilters(): Promise<Record<string, any>>;

	abstract healthCheck(): Promise<HealthCheckResponse>;

	cancel?(): void;
}

/* ============================================================================
 * Mock implementation
 * ========================================================================== */

export class MockSearchService extends SearchService {
	async initialize(_config: Record<string, any>): Promise<void> {
	}

	async search(_query: string, _options: SearchOptions = {}): Promise<SearchResponse> {
		return {
			results: [],
			total: 0,
			took: 0,
			provider: "mock",
		};
	}

	async suggest(_query: string, _options: SearchOptions = {}): Promise<string[]> {
		return [];
	}

	async getFilters(): Promise<Record<string, any>> {
		return {};
	}

	async healthCheck(): Promise<HealthCheckResponse> {
		return { healthy: true, message: "Mock service is operational (placeholder)" };
	}

	cancel(): void {
	}
}

/* ============================================================================
 * Factory
 * ========================================================================== */

export type SearchServiceType = "mock" | "meilisearch" | "api";

export function createSearchService(type: SearchServiceType = "mock", _config: Record<string, any> = {}): SearchService {
	switch (type) {
		case "mock":
			return new MockSearchService();

		case "meilisearch":
			return new MockSearchService();

		case "api":
			return new MockSearchService();

		default:
			return new MockSearchService();
	}
}

export default createSearchService;
